<script>
import LandLeaseService from "@/services/LandLeaseService";
import StakeholderService from "@/services/StakeholderService";
import ProjectService from "@/services/ProjectService";
import UserService from "@/services/UserService";
import Cloner from "@/libraries/Cloner";
import { LeaseType, PartyType, LeaseStatus } from "@/constants/LandLease.const";

export default {
    props: {
        context: {},
        projectId: {}
    },
    data() {
        return {
            editing: false,
            loading: true,
            saving: false,
            errors: false,
            projects: [],
            users: [],
            parties: [],
            entity: {
                id: null,
                project_id: null,
                user_id: null,
                lease_type: null,
                party_type: null,
                status: null,
                party_name: null,
                party_email: null,
                party_phone: null,
                contract_url: null,
                contract_url_name: null,
                warning_days: 90,
                start_date: null,
                expiration_date: null,
                notes: null
            }
        };
    },
    computed: {
        leaseTypes() {
            return LeaseType;
        },
        partyTypes() {
            return PartyType;
        },
        leaseStatuses() {
            return LeaseStatus;
        },
        selectedProject() {
            return this.entity.project_id;
        }
    },
    watch: {
        selectedProject(nV) {
            if (nV) {
                this.fetchParties(nV);
            }
        }
    },
    async created() {
        if (!this.context.empty) {
            this.editing = true;
            this.entity = Cloner.pickClone(this.context, [
                "id",
                "project_id",
                "user_id",
                "lease_type",
                "party_type",
                "status",
                "party_name",
                "party_email",
                "party_phone",
                "contract_url",
                "contract_url_name",
                "warning_days",
                "start_date",
                "expiration_date",
                "notes"
            ]);
        } else {
            if (this.projectId) {
                this.entity.project_id = this.projectId;
            }
        }
        await Promise.all([this.fetchProjects(), this.fetchUsers()]);
        if (this.entity.project_id) {
            await this.fetchParties(this.entity.project_id);
        }
        this.loading = false;
    },
    methods: {
        async fetchProjects() {
            try {
                const r = await ProjectService.GET_dropdown({
                    invoice_setup: "projects",
                    only_projects: 1,
                    status: [1, 5]
                });
                this.projects = r.data.data;
            } catch (e) {
                console.log("fetchProjects e", e);
            }
        },
        async fetchUsers() {
            try {
                const r = await UserService.dropdown();
                this.users = r.data.data;
            } catch (e) {
                console.log("fetchUsers e", e);
            }
        },
        async fetchParties(projectId) {
            try {
                const [leasesRes, stakeholdersRes] = await Promise.all([
                    LandLeaseService.GET({ project_id: projectId }),
                    StakeholderService.GET({ project_id: projectId })
                ]);
                const seen = new Set();
                const partyOptions = (leasesRes.data.data || [])
                    .filter(l => l.party_name && !seen.has(l.party_name))
                    .map(l => {
                        seen.add(l.party_name);
                        return { value: l.party_name, label: l.party_name };
                    });
                const stakeholderOptions = (stakeholdersRes.data.data || [])
                    .filter(s => s.name && !seen.has(s.name))
                    .map(s => {
                        seen.add(s.name);
                        return {
                            value: s.name,
                            label: `${s.name} (${this.$t(
                                "land_leases.stakeholder"
                            )})`
                        };
                    });
                this.parties = [...partyOptions, ...stakeholderOptions];
            } catch (e) {
                console.log("fetchParties e", e);
            }
        },
        async saveForm() {
            this.saving = true;
            try {
                if (this.editing) {
                    await LandLeaseService.PUT(this.entity);
                } else {
                    await LandLeaseService.POST(this.entity);
                }
                this.$emit("close-reload");
            } catch (e) {
                this.errors = e.response.data.errors;
            }
            this.saving = false;
        },
        async deleteEntity() {
            this.$swal({
                icon: "warning",
                text: this.$t("land_leases.confirm_delete"),
                showConfirmButton: true,
                showCancelButton: true
            }).then(async confirmed => {
                if (confirmed.isConfirmed) {
                    try {
                        await LandLeaseService.DELETE(this.entity.id);
                        this.$notify({
                            message: this.$t("land_leases.notify_deleted"),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                        this.$emit("close-reload");
                    } catch (e) {}
                }
            });
        }
    }
};
</script>

<template>
    <modal
        size="xl"
        :show="true"
        @close="$emit('close')"
        class="budget-modal"
        :loading="loading"
    >
        <template slot="header">
            <template v-if="editing">
                {{ $t("land_leases.edit_lease") }}
            </template>
            <template v-else>
                {{ $t("land_leases.create_lease") }}
            </template>
        </template>

        <template slot="default">
            <FormulateForm :errors="errors" @submit="saveForm" v-model="entity">
                <div class="row">
                    <FormulateErrors />

                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="project_id"
                            type="multiselect"
                            :options="projects"
                            :label="$t('land_leases.project')"
                            :placeholder="$t('land_leases.placeholder_project')"
                            validation="required"
                            :validation-name="$t('land_leases.project')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="user_id"
                            type="multiselect"
                            :options="users"
                            :label="$t('land_leases.pm')"
                            :placeholder="$t('land_leases.placeholder_pm')"
                            validation="required"
                            :validation-name="$t('land_leases.pm')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="lease_type"
                            type="select"
                            :options="leaseTypes"
                            :label="$t('land_leases.lease_type')"
                            :placeholder="
                                $t('land_leases.placeholder_lease_type')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.lease_type')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="status"
                            type="select"
                            :options="leaseStatuses"
                            :label="$t('land_leases.status')"
                            :placeholder="$t('land_leases.placeholder_status')"
                            validation="required"
                            :validation-name="$t('land_leases.status')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="party_type"
                            type="select"
                            :options="partyTypes"
                            :label="$t('land_leases.party_type')"
                            :placeholder="
                                $t('land_leases.placeholder_party_type')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.party_type')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="party_name"
                            type="multiselect"
                            :options="parties"
                            :taggable="true"
                            :label="$t('land_leases.party_name')"
                            :placeholder="
                                $t('land_leases.placeholder_party_name')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.party_name')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="party_email"
                            type="text"
                            :label="$t('land_leases.party_email')"
                            :placeholder="
                                $t('land_leases.placeholder_party_email')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.party_email')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="party_phone"
                            type="text"
                            :label="$t('land_leases.party_phone')"
                            :placeholder="
                                $t('land_leases.placeholder_party_phone')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.party_phone')"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="start_date"
                            type="date"
                            :label="$t('land_leases.start_date')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="expiration_date"
                            type="date"
                            :label="$t('land_leases.expiration_date')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="contract_url"
                            type="text"
                            :label="$t('land_leases.contract_url')"
                            :placeholder="
                                $t('land_leases.placeholder_contract_url')
                            "
                            validation="required"
                            :validation-name="$t('land_leases.contract_url')"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <FormulateInput
                            name="warning_days"
                            type="number"
                            :label="
                                $t('land_leases.remind_renewal_days_before')
                            "
                            :placeholder="
                                $t('land_leases.remind_renewal_days_before')
                            "
                            validation="required"
                            :validation-name="
                                $t('land_leases.remind_renewal_days_before')
                            "
                        />
                    </div>
                    <div class="col-12">
                        <FormulateInput
                            name="notes"
                            type="textarea"
                            :label="$t('land_leases.notes')"
                            :placeholder="$t('land_leases.placeholder_notes')"
                            rows="5"
                        />
                    </div>

                    <div class="col-12">
                        <div class="row mt-4">
                            <div class="col">
                                <base-button
                                    type="danger"
                                    :outline="true"
                                    @click="deleteEntity"
                                    v-if="editing"
                                >
                                    <i class="far fa-trash"></i>
                                    {{ $t("delete") }}
                                </base-button>
                            </div>
                            <div class="col text-right">
                                <base-button
                                    type="secondary"
                                    :outline="true"
                                    @click="$emit('close')"
                                >
                                    {{ $t("cancel") }}
                                </base-button>
                                <base-button
                                    type="primary"
                                    :loading="saving"
                                    native-type="submit"
                                >
                                    {{ $t("save") }}
                                </base-button>
                            </div>
                        </div>
                    </div>
                </div>
            </FormulateForm>
        </template>
    </modal>
</template>
